import { observer } from 'mobx-react-lite'
import Styles from './NavMenuItem.module.scss'
import { ReactNode } from 'react'
import { Icon } from 'almanac-ui/icons/Types'
import { NavItem } from '../../NavItem'
import { NavItemProps } from '../../NavItem/Types'
import { matchPath, useLocation } from 'react-router-dom'
import { Shimmerer } from 'blaze-ui/Shimmerer'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import cx from 'classnames'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

interface Props extends Omit<NavItemProps, 'padLeft' | 'classNames' | 'elementType' | 'children'> {
  label: string
  children?: ReactNode | ((isActive: boolean) => ReactNode)
  icon: Icon
  activeIcon?: Icon
  boldActiveLabel?: boolean
}

export const NavMenuItem = observer(({ label, icon: Icon, children, boldActiveLabel = true, ...navProps }: Props) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const location = useLocation()
  const isActive = Boolean(
    matchPath(location.pathname, {
      path: typeof navProps.to === 'string' ? navProps.to : navProps.to?.pathname,
      exact: navProps.exact ?? false,
    }),
  )
  const { activeIcon: ActiveIcon } = navProps
  const { isMobile } = useResponsiveDesign()
  const desktopIconSize = isSystemRedesignEnabled ? 18 : 20
  const iconSize = isMobile ? 24 : desktopIconSize

  return (
    <ConditionalWrapper
      condition={!isActive && !isMobile}
      wrapper={(children) => <Shimmerer variant="secondary">{children}</Shimmerer>}
    >
      <NavItem.Root size="lg" {...navProps} classNames={Styles.root}>
        {isActive && ActiveIcon ? (
          <ActiveIcon size={iconSize} color="var(--dark-80)" />
        ) : (
          <Icon size={iconSize} color="var(--dark-60)" />
        )}
        <NavItem.Label
          label={label}
          classNames={cx(Styles.label, { [Styles.isActive]: isActive && boldActiveLabel })}
        />
        {children && (
          <div className={Styles.children}>{typeof children === 'function' ? children(isActive) : children}</div>
        )}
      </NavItem.Root>
    </ConditionalWrapper>
  )
})
